import { useState } from "react";
import { Syringe, Trash2 } from "lucide-react";
import { Label } from "./ui-bits";
import { api } from "@/lib/auth";

export const MEDS = [
  { id: "cypionate", label: "Testosterone cypionate (injection)" },
  { id: "enanthate", label: "Testosterone enanthate (injection)" },
  { id: "sustanon", label: "Sustanon (injection)" },
  { id: "nebido", label: "Testosterone undecanoate — depot injection" },
  { id: "cream", label: "Testosterone cream" },
  { id: "tablet", label: "Oral testosterone" },
  { id: "other", label: "Other" },
];
export const medLabel = (id) => MEDS.find((m) => m.id === id)?.label || id;

/** Days between a result's sample date and the latest dose logged on/before it. */
export function daysSinceDose(takenAt, doses) {
  let best = null;
  for (const d of doses) {
    const day = d.taken_at.slice(0, 10);
    if (day <= takenAt && (best === null || day > best)) best = day;
  }
  if (best === null) return null;
  return Math.round((new Date(takenAt) - new Date(best)) / 86400000);
}

export default function DoseDiary({ doses, setDoses, readOnly = false }) {
  const [form, setForm] = useState({ taken_at: "", medication: "cypionate", dose_mg: "", volume_ml: "", site: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const injectable = !["cream", "tablet"].includes(form.medication);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const doc = await api("/doses", { method: "POST", body: { ...form, dose_mg: form.dose_mg ? Number(form.dose_mg) : null, volume_ml: form.volume_ml ? Number(form.volume_ml) : null, site: form.site || null } });
      setDoses((d) => [doc, ...d].sort((a, b) => b.taken_at.localeCompare(a.taken_at)));
      setForm((f) => ({ ...f, dose_mg: "", volume_ml: "", site: "" }));
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api(`/doses/${id}`, { method: "DELETE" });
    setDoses((d) => d.filter((x) => x.dose_id !== id));
  };

  const last = doses[0];
  const sinceLast = last ? Math.floor((Date.now() - new Date(last.taken_at)) / 86400000) : null;

  return (
    <div className="card-line rounded-2xl p-6 sm:p-8" data-testid="dose-diary">
      <div className="flex items-start justify-between gap-4">
        <Label><Syringe size={14} /> Dose diary</Label>
        {last && <p className="mono-label text-dusk text-right" data-testid="dose-last">Last dose {sinceLast === 0 ? "today" : `${sinceLast} day${sinceLast === 1 ? "" : "s"} ago`}</p>}
      </div>
      <p className="text-sm text-mute leading-relaxed mt-3">Log each dose so your blood results can be read against timing — a trough sample the day before an injection reads very differently from a peak sample two days after.</p>

      {!readOnly && (
        <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5" data-testid="dose-form">
          <label className="block"><span className="mono-label text-dusk block mb-2">Date & time</span><input type="datetime-local" required className="field" value={form.taken_at} onChange={set("taken_at")} data-testid="dose-datetime" /></label>
          <label className="block"><span className="mono-label text-dusk block mb-2">Medication</span>
            <select className="field" value={form.medication} onChange={set("medication")} data-testid="dose-medication">{MEDS.map((m) => <option key={m.id} value={m.id} className="bg-surface">{m.label}</option>)}</select>
          </label>
          <label className="block"><span className="mono-label text-dusk block mb-2">Dose (mg)</span><input type="number" step="any" min="0" className="field" value={form.dose_mg} onChange={set("dose_mg")} placeholder="e.g. 100" data-testid="dose-mg" /></label>
          {injectable && (
            <>
              <label className="block"><span className="mono-label text-dusk block mb-2">Volume (mL)</span><input type="number" step="0.01" min="0" className="field" value={form.volume_ml} onChange={set("volume_ml")} placeholder="e.g. 0.5" data-testid="dose-ml" /></label>
              <label className="block"><span className="mono-label text-dusk block mb-2">Site</span><input className="field" value={form.site} onChange={set("site")} placeholder="e.g. Left glute, right thigh" data-testid="dose-site" /></label>
            </>
          )}
          <div className="flex items-end">
            <button type="submit" disabled={busy} className="btn-ghost !py-2.5 !px-5 text-sm" data-testid="dose-submit">{busy ? "Saving…" : "Log dose"}</button>
          </div>
          {error && <p className="text-rose text-xs sm:col-span-2 lg:col-span-3" data-testid="dose-error">{error}</p>}
        </form>
      )}

      {doses.length > 0 && (
        <ul className="mt-6 divide-y divide-line text-xs" data-testid="dose-list">
          {doses.slice(0, 12).map((d) => (
            <li key={d.dose_id} className="py-2.5 flex items-center gap-3" data-testid={`dose-row-${d.dose_id}`}>
              <span className="font-mono text-dusk whitespace-nowrap">{d.taken_at.replace("T", " ")}</span>
              <span className="text-bone/85 flex-1">{medLabel(d.medication)}{d.dose_mg ? ` · ${d.dose_mg} mg` : ""}{d.volume_ml ? ` · ${d.volume_ml} mL` : ""}{d.site ? <span className="text-dusk"> · {d.site}</span> : null}</span>
              {!readOnly && <button type="button" onClick={() => remove(d.dose_id)} className="text-dusk hover:text-rose transition-colors" aria-label="Delete dose" data-testid={`dose-delete-${d.dose_id}`}><Trash2 size={14} /></button>}
            </li>
          ))}
        </ul>
      )}
      {doses.length > 12 && <p className="mono-label text-dusk mt-3">Showing latest 12 of {doses.length}</p>}
    </div>
  );
}
